import { message } from "antd";
import isEqual from "lodash/isEqual";
import { useCallback, useMemo, useRef } from "react";
import type { CommercialBillingScheduleBreakdownItem } from "../../../../../../types/bookings";
import { extractValue } from "../../../../../libs/utils";
import type { ServiceCharge } from "./api";
import { getBillingScheduleRentSumErrorMessagesForPlans } from "./billing-schedule/rentSumValidation";
import { isBreakdownSequenceValid, toSequenceNumberOrNull } from "./billing-schedule/sequenceValidation";
import { normalizePlansForSendApprovalSnapshot } from "./breakdownInputSnapshots";
import {
  normalizeCommercialBreakdownFromBookingDetails,
  normalizeCommercialBreakdownFromPlans,
} from "./commercialBreakdownComparison";
import { hasNonNegativeNumber, hasPositiveNumber, hasValue } from "./utils";
import type { PlanFieldItem, ServiceChargePlan } from "./year-card/YearCard";

type UseBreakdownStaleRegistryOptions = {
  plansFields: PlanFieldItem[];
  isSeparateInvoicingEnabled: boolean;
  plansWatch: unknown[];
  savedPaymentPlan: unknown[] | undefined | null;
  savedCommercialBreakdown: CommercialBillingScheduleBreakdownItem[] | undefined | null;
  isDirty: boolean;
  yearDifference: number;
  isTurnoverRentOnly: boolean;
  requiresTorPeriod: boolean;
  paymentPlanApproval: string | undefined;
};

type UseBreakdownStaleRegistryReturn = {
  registerPaymentPlanBreakdownStale: (planId: string, stale: boolean) => void;
  registerServiceChargeBreakdownStale: (planId: string, stale: boolean) => void;
  assertNoStaleCommercialBreakdowns: () => boolean;
  showSendForApproval: boolean;
};

export function useBreakdownStaleRegistry(options: UseBreakdownStaleRegistryOptions): UseBreakdownStaleRegistryReturn {
  const {
    plansFields,
    isSeparateInvoicingEnabled,
    plansWatch,
    savedPaymentPlan,
    savedCommercialBreakdown,
    isDirty,
    yearDifference,
    isTurnoverRentOnly,
    requiresTorPeriod,
    paymentPlanApproval,
  } = options;

  const staleRef = useRef<{
    payment: Record<string, boolean>;
    service: Record<string, boolean>;
  }>({
    payment: {},
    service: {},
  });

  const registerPaymentPlanBreakdownStale = useCallback((planId: string, stale: boolean) => {
    staleRef.current.payment[planId] = stale;
  }, []);

  const registerServiceChargeBreakdownStale = useCallback((planId: string, stale: boolean) => {
    staleRef.current.service[planId] = stale;
  }, []);

  const assertNoStaleCommercialBreakdowns = useCallback((): boolean => {
    if (!isSeparateInvoicingEnabled) return true;

    const incompleteBreakdownMessages = getIncompleteBillingBreakdownMessages(plansWatch, {
      isTurnoverRentOnly,
      requiresTorPeriod,
    });

    if (incompleteBreakdownMessages.length > 0) {
      message.warning(`${incompleteBreakdownMessages.join("; ")} before continuing.`);
      return false;
    }

    const rentSumErrorMessages = getBillingScheduleRentSumErrorMessagesForPlans(plansWatch, { isTurnoverRentOnly });

    if (rentSumErrorMessages.length > 0) {
      message.error(rentSumErrorMessages.join("; "));
      return false;
    }

    const affectedByYear = plansFields
      .map((field, index) => {
        const affected: string[] = [];
        if (staleRef.current.payment[field.id]) affected.push("payment plan fields");
        if (staleRef.current.service[field.id]) affected.push("service charge fields or schedule rows");

        if (affected.length === 0) return null;

        return `Year ${field.noOfYear ?? index + 1}: ${affected.join(", ")}`;
      })
      .filter(Boolean);

    if (affectedByYear.length === 0) return true;

    message.warning(
      `${affectedByYear.join("; ")} changed after a breakdown was generated. Click "Generate Breakdown" again.`,
    );

    return false;
  }, [isSeparateInvoicingEnabled, isTurnoverRentOnly, plansFields, plansWatch, requiresTorPeriod]);

  const comparisonOptions = useMemo(() => ({ isTurnoverRentOnly }), [isTurnoverRentOnly]);

  const normalizedFormPlans = useMemo(
    () => normalizePlansForSendApprovalSnapshot(plansWatch, comparisonOptions),
    [plansWatch, comparisonOptions],
  );
  const normalizedSavedPlans = useMemo(
    () => normalizePlansForSendApprovalSnapshot(savedPaymentPlan, comparisonOptions),
    [savedPaymentPlan, comparisonOptions],
  );

  const normalizedFormCommercialBreakdown = useMemo(
    () => normalizeCommercialBreakdownFromPlans(plansWatch, comparisonOptions),
    [plansWatch, comparisonOptions],
  );
  const normalizedSavedCommercialBreakdown = useMemo(
    () => normalizeCommercialBreakdownFromBookingDetails(savedCommercialBreakdown, comparisonOptions),
    [savedCommercialBreakdown, comparisonOptions],
  );

  const yearCardsMatchExpectedYears = isTurnoverRentOnly || plansFields.length === yearDifference;
  const allExpectedYearCardsComplete = useMemo(
    () =>
      areExpectedPaymentPlanCardsComplete(plansWatch, {
        expectedYearCount: yearDifference,
        isTurnoverRentOnly,
        requiresTorPeriod,
      }),
    [plansWatch, yearDifference, isTurnoverRentOnly, requiresTorPeriod],
  );
  const hasPaymentPlanFieldChange = !isEqual(normalizedFormPlans, normalizedSavedPlans);
  const hasCommercialBreakdownChange = !isEqual(normalizedFormCommercialBreakdown, normalizedSavedCommercialBreakdown);
  const hasMeaningfulChange = hasPaymentPlanFieldChange || hasCommercialBreakdownChange;

  const showSendForApproval = isSeparateInvoicingEnabled
    ? paymentPlanApproval !== "disable" &&
      yearCardsMatchExpectedYears &&
      allExpectedYearCardsComplete &&
      hasMeaningfulChange
    : isDirty && paymentPlanApproval !== "disable";

  return {
    registerPaymentPlanBreakdownStale,
    registerServiceChargeBreakdownStale,
    assertNoStaleCommercialBreakdowns,
    showSendForApproval,
  };
}

/**
 * Returns true only when the service charge section is fully ready:
 * all required fields filled AND the breakdown schedule has been generated.
 */
function isServiceChargeComplete(
  sc: ServiceChargePlan | undefined,
  scheduleRows: ServiceCharge[] | undefined,
): boolean {
  if (!sc) return false;

  const scType = extractValue(sc.rentType);
  // areaSqft and percentage require a chargeValue; fixed does not.
  const requiresChargeValue = scType === "areaSqft" || scType === "percentage";

  return (
    hasValue(sc.rentType) &&
    hasNonNegativeNumber(sc.annualRent) &&
    hasValue(sc.installments) &&
    (!requiresChargeValue || hasPositiveNumber(sc.chargeValue)) &&
    (scheduleRows?.length ?? 0) > 0
  );
}

function isPaymentPlanCardComplete(
  plan: PlanFieldItem | undefined,
  index: number,
  options: { isTurnoverRentOnly: boolean; requiresTorPeriod: boolean },
): boolean {
  if (!plan) return false;

  const rentType = extractValue(plan.rentType);
  const isAreaSqft = rentType === "areaSqft";

  const hasInstallments = options.isTurnoverRentOnly || hasValue(plan.installments);
  const hasRentType = hasValue(plan.rentType);
  const hasRent = options.isTurnoverRentOnly
    ? hasNonNegativeNumber(plan.annualRent)
    : hasPositiveNumber(plan.annualRent);
  const hasTorPeriod = !options.requiresTorPeriod || hasValue(plan.torPeriod);
  const hasSecurityDeposit = index > 0 || hasNonNegativeNumber(plan.securityDeposit);
  const hasPerSqftFields = !isAreaSqft || (hasPositiveNumber(plan.rentValue) && hasPositiveNumber(plan.unitAreaInSqft));

  // Billing schedule breakdown must have been generated and row assignments must be valid.
  const isBillingBreakdownComplete = areBillingScheduleRowsComplete(plan, options);

  // If the service charge section is active, it must be fully complete too.
  const hasServiceChargeSection = plan.serviceCharge?.rentType != null;
  const serviceChargeReady =
    !hasServiceChargeSection || isServiceChargeComplete(plan.serviceCharge, plan.serviceChargeSchedule);

  return (
    hasInstallments &&
    hasRentType &&
    hasRent &&
    hasTorPeriod &&
    hasSecurityDeposit &&
    hasPerSqftFields &&
    isBillingBreakdownComplete &&
    serviceChargeReady
  );
}

function areBillingScheduleRowsComplete(
  plan: PlanFieldItem,
  options: { isTurnoverRentOnly: boolean; requiresTorPeriod: boolean },
): boolean {
  const rows = plan.billingScheduleRows;
  if (!rows?.length) return false;

  const hasRequiredRowValues = rows.every(
    (row) =>
      (options.isTurnoverRentOnly || hasNonNegativeNumber(row.baseRent)) &&
      (options.isTurnoverRentOnly || hasValue(row.installment)) &&
      (!options.requiresTorPeriod || hasValue(row.torPeriod)),
  );

  if (!hasRequiredRowValues) return false;

  const maxInstallments = toSequenceNumberOrNull(extractValue(plan.installments));
  const maxTorPeriods = toSequenceNumberOrNull(extractValue(plan.torPeriod));

  return (
    (options.isTurnoverRentOnly ||
      isBreakdownSequenceValid(
        rows.map((row) => row.installment),
        maxInstallments,
      )) &&
    (!options.requiresTorPeriod ||
      isBreakdownSequenceValid(
        rows.map((row) => row.torPeriod),
        maxTorPeriods,
      ))
  );
}

function getIncompleteBillingBreakdownMessages(
  plans: unknown[] | undefined | null,
  options: { isTurnoverRentOnly: boolean; requiresTorPeriod: boolean },
): string[] {
  if (!Array.isArray(plans)) return [];

  return plans.flatMap((raw, index) => {
    const plan = raw as PlanFieldItem;
    const rows = plan.billingScheduleRows;
    if (!rows?.length) return [];

    const missingFields = new Set<string>();
    rows.forEach((row) => {
      if (!options.isTurnoverRentOnly && !hasNonNegativeNumber(row.baseRent)) missingFields.add("base rent");
      if (!options.isTurnoverRentOnly && !hasValue(row.installment)) missingFields.add("installment numbers");
      if (options.requiresTorPeriod && !hasValue(row.torPeriod)) missingFields.add("TOR period numbers");
    });

    const invalidFields = new Set<string>();
    const maxInstallments = toSequenceNumberOrNull(extractValue(plan.installments));
    const maxTorPeriods = toSequenceNumberOrNull(extractValue(plan.torPeriod));

    if (
      !options.isTurnoverRentOnly &&
      missingFields.has("installment numbers") === false &&
      !isBreakdownSequenceValid(
        rows.map((row) => row.installment),
        maxInstallments,
      )
    ) {
      invalidFields.add("installment sequence");
    }

    if (
      options.requiresTorPeriod &&
      missingFields.has("TOR period numbers") === false &&
      !isBreakdownSequenceValid(
        rows.map((row) => row.torPeriod),
        maxTorPeriods,
      )
    ) {
      invalidFields.add("TOR period sequence");
    }

    if (missingFields.size === 0 && invalidFields.size === 0) return [];

    const details = [
      ...(missingFields.size > 0 ? [`fill ${Array.from(missingFields).join(", ")}`] : []),
      ...(invalidFields.size > 0 ? [`fix ${Array.from(invalidFields).join(", ")}`] : []),
    ].join(" and ");

    return [`Year ${plan.noOfYear ?? index + 1}: Please ${details} in billing schedule breakdown`];
  });
}

function areExpectedPaymentPlanCardsComplete(
  plans: unknown[] | undefined | null,
  options: { expectedYearCount: number; isTurnoverRentOnly: boolean; requiresTorPeriod: boolean },
): boolean {
  if (!Array.isArray(plans)) return false;
  const expectedCount = options.isTurnoverRentOnly ? 1 : options.expectedYearCount;
  if (plans.length !== expectedCount) return false;

  return plans.every((plan, index) => isPaymentPlanCardComplete(plan as PlanFieldItem, index, options));
}
